import {
  addWeeks,
  eachDayOfInterval,
  isSameDay,
  previousMonday,
  previousSunday,
} from 'date-fns';

import { CalendarDayData, CalendarStartDay, CalendarStartDays } from './types';
import type { Event } from '@/types';

const startDayMap: Record<CalendarStartDay, typeof previousSunday> = {
  [CalendarStartDays.SUNDAY]: previousSunday,
  [CalendarStartDays.MONDAY]: previousMonday,
};

/**
 * Given a Date, this function returns an array of days that stops
 * at the designated number of weeks. Its purpose is to create a
 * calendar that we may insert data into for each day.
 */
export function getListOfDays(
  startDate: Date | number,
  startDay: CalendarStartDay,
  lengthInWeeks: number = 10,
) {
  const start = startDayMap[startDay](startDate);
  const end = addWeeks(start, lengthInWeeks);
  return eachDayOfInterval({ start, end });
}

export function getCalendarOfEvents(
  events: Event[],
  startDay: CalendarStartDay,
): CalendarDayData[] {
  if (!events.length) {
    return [];
  }

  const firstEventDate = new Date(events[0].start);
  const lengthInWeeks = 10; // How many weeks of calendar should we show?

  return getListOfDays(firstEventDate, startDay, lengthInWeeks).map((date) => {
    const eventsOnThisDate = events.filter(({ start }) =>
      isSameDay(new Date(start), new Date(date)),
    );
    return {
      date,
      events: eventsOnThisDate,
    };
  });
}

export function getDaysOfTheWeek(startDay: CalendarStartDay) {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const startDayIndex = days.findIndex(
    (day) => day.toLowerCase() === startDay.toLowerCase(),
  );
  const daysBeforeStartDay = days.splice(0, startDayIndex);
  return [...days, ...daysBeforeStartDay];
}

export function getStartDayByLanguage(language: string | null) {
  if (language == 'en_us') {
    return CalendarStartDays.MONDAY;
  }
  return CalendarStartDays.SUNDAY;
}
